import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Alert,
} from "@mui/material";
import { Formik, Form, Field, getIn } from "formik";
import * as Yup from "yup";
import { ProjectModule, CreateProjectRequest, ProjectStatus } from "../types";
import {
  commonTextFieldProps,
  commonDateFieldProps,
  commonButtonProps,
  smallButtonProp,
} from "../../../utils/styles";
import SearchableAutocomplete from "../../../components/SearchableAutocomplete";
import { Client } from "../../../pages/masterData/client/types";
import { formatText } from "../../../utils/utils";
import { PROJECT_TERMINOLOGY } from "../enums";

interface ModuleActionModalProps {
  open: boolean;
  onClose: () => void;
  module?: ProjectModule | null;
  onSubmit: (data: CreateProjectRequest) => Promise<void>;
  isSubmitting: boolean;
}

const validationSchema = Yup.object({
  name: Yup.string()
    .required(`${PROJECT_TERMINOLOGY.SINGULAR} name is required`)
    .min(3, "Name must be at least 3 characters"),
  description: Yup.string(),
  startDate: Yup.date().required("Start date is required"),
  endDate: Yup.date().min(
    Yup.ref("startDate"),
    "End date must be after start date"
  ).nullable(),
  clientId: Yup.string().required("Client is required"),
});

const ModuleActionModal: React.FC<ModuleActionModalProps> = ({
  open,
  onClose,
  module,
  onSubmit,
  isSubmitting,
}) => {
  const isEditMode = !!module;

  const initialValues: CreateProjectRequest = {
    name: module?.name || "",
    description: module?.description || "",
    startDate: module?.startDate
      ? new Date(module.startDate).toISOString().split("T")[0]
      : new Date().toISOString().split("T")[0],
    durationInMonths:
      module?.startDate && module?.endDate
        ? (
            (new Date(module.endDate).getTime() -
              new Date(module.startDate).getTime()) /
            (1000 * 60 * 60 * 24 * 30)
          ).toFixed(0)
        : "",
    endDate: module?.endDate || null,
    clientId: module?.clientId || "",
    client: module?.client || undefined,
  };

  const handleSubmit = async (values: CreateProjectRequest) => {
    // Ensure clientId is set from selected client
    const submitData = {
      ...values,
      clientId: values.client?.id || values.clientId,
      client: values.client || undefined,
    };

    await onSubmit(submitData);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 500 }}>
          {isEditMode
            ? `Edit ${PROJECT_TERMINOLOGY.SINGULAR}`
            : `Add New ${PROJECT_TERMINOLOGY.SINGULAR}`}
        </Typography>
      </DialogTitle>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
        }) => (
          <Form>
            <DialogContent dividers>
              <Grid container spacing={2}>
                {!isEditMode && (
                  <Grid item xs={6}>
                    <SearchableAutocomplete
                      entityType="CLIENT"
                      value={values.client || null}
                      onSelect={(option) => {
                        const client = option as Client;
                        setFieldValue("clientId", client.id);
                        setFieldValue("client", client);
                      }}
                      onChange={(value) => {}}
                      label="Client"
                      placeholder="Search or enter manually..."
                      getOptionLabel={(option) =>
                        (option as Client).clientName || ""
                      }
                      renderOptionContent={(option) => option.clientName || ""}
                      noOptionsText="No options found"
                      disabled={isSubmitting}
                      size="small"
                    />
                    {touched.clientId && errors.clientId && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        {errors.clientId}
                      </Typography>
                    )}
                  </Grid>
                )}

                {/* Display selected client information */}
                {(values.client || module?.clientId) && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "primary.light",
                        bgcolor: "#F0F7FF",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        color="primary.dark"
                        sx={{ fontSize: "0.75rem" }}
                        gutterBottom
                      >
                        Client Details (From Master Data)
                      </Typography>
                      <Grid container spacing={1}>
                        {Object.entries(
                          (values.client || module?.client) as Client
                        ).map(([key, value]) => {
                          if (key === "id" || key === "companyId") return null;
                          return (
                            <Grid item xs={12} sm={6} md={3} key={key}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: "0.75rem" }}
                              >
                                {formatText(key)}: {value}
                              </Typography>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  </Grid>
                )}

                <Grid container item xs={12} spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      name="name"
                      label={`${PROJECT_TERMINOLOGY.SINGULAR} Name`}
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.name && !!errors.name}
                      helperText={touched.name && errors.name}
                      {...commonTextFieldProps}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      type="date"
                      name="startDate"
                      label="Start Date"
                      value={values.startDate}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.startDate && !!errors.startDate}
                      helperText={touched.startDate && errors.startDate}
                      disabled={isEditMode}
                      {...commonDateFieldProps}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Field
                      as={TextField}
                      name={`durationInMonths`}
                      label="Duration (Months)"
                      type="number"
                      fullWidth
                      placeholder="e.g., 6"
                      inputProps={{
                        min:
                          isEditMode &&
                          module &&
                          "startDate" in module &&
                          "endDate" in module &&
                          module.startDate &&
                          module.endDate
                            ? Math.min(
                                Math.ceil(
                                  (new Date(module.endDate).getTime() -
                                    new Date(module.startDate).getTime()) /
                                    (1000 * 60 * 60 * 24 * 30)
                                ),
                                Number(initialValues.durationInMonths)
                              )
                            : 1,
                        max: 60,
                        onInvalid: (e: React.FormEvent<HTMLInputElement>) => {
                          (e.target as HTMLInputElement).setCustomValidity(
                            "Duration cannot be decreased."
                          );
                        },
                        onInput: (e: React.FormEvent<HTMLInputElement>) => {
                          (e.target as HTMLInputElement).setCustomValidity("");
                        },
                      }}
                      error={
                        getIn(touched, `durationInMonths`) &&
                        Boolean(getIn(errors, `durationInMonths`))
                      }
                      helperText={
                        getIn(touched, `durationInMonths`) &&
                        getIn(errors, `durationInMonths`)
                      }
                      disabled={isSubmitting}
                      onChange={(e: any) => {
                        const durationStr = e.target.value;
                        const duration = parseFloat(durationStr);
                        setFieldValue(`durationInMonths`, durationStr);

                        // Auto-calculate end date
                        const startDate = values.startDate;
                        if (startDate && !isNaN(duration) && duration > 0) {
                          const start = new Date(startDate);
                          const end = new Date(start);
                          end.setMonth(start.getMonth() + duration);
                          setFieldValue(
                            `endDate`,
                            end.toISOString().split("T")[0]
                          );
                        }
                      }}
                      {...commonTextFieldProps}
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    name="description"
                    label="Description"
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.description && !!errors.description}
                    helperText={touched.description && errors.description}
                    {...commonTextFieldProps}
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
              <Button
                onClick={onClose}
                disabled={isSubmitting}
                variant="outlined"
                {...commonButtonProps}
                color="error"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                {...commonButtonProps}
              >
                {isSubmitting ? "Saving..." : isEditMode ? "Update" : "Create"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default ModuleActionModal;
